'use client';

import { useRef, useEffect } from 'react';
import { type Room, summarizeOccupancy } from '../config';

export type { Room };

interface OccupancyPickerProps {
  value: Room[];
  onChange: (rooms: Room[]) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  maxRooms?: number;
  maxAdults?: number;
  maxChildren?: number;
}

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

const CHILD_AGES = Array.from({ length: 18 }, (_, i) => i);

function Stepper({
  label, sub, value, min, max, onChange,
}: {
  label: string; sub?: string; value: number; min: number; max: number; onChange: (v: number) => void;
}) {
  return (
    <div className="sb-stepper">
      <div className="sb-stepper__info">
        <p className="sb-stepper__label">{label}</p>
        {sub && <p className="sb-stepper__sub">{sub}</p>}
      </div>
      <div className="sb-stepper__controls">
        <button type="button" className="sb-stepper__btn" onClick={() => onChange(clamp(value - 1, min, max))} disabled={value <= min} aria-label={`Decrease ${label}`}>−</button>
        <span className="sb-stepper__val" aria-live="polite">{value}</span>
        <button type="button" className="sb-stepper__btn" onClick={() => onChange(clamp(value + 1, min, max))} disabled={value >= max} aria-label={`Increase ${label}`}>+</button>
      </div>
    </div>
  );
}

export default function OccupancyPicker({
  value, onChange, open, onOpenChange,
  maxRooms = 10, maxAdults = 30, maxChildren = 10,
}: OccupancyPickerProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (open && ref.current && !ref.current.contains(e.target as Node)) {
        onOpenChange(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onOpenChange]);

  function updateAdults(index: number, val: number) {
    onChange(value.map((r, i) => (i === index ? { ...r, adults: val } : r)));
  }

  function updateChildren(index: number, count: number) {
    const room = value[index];
    const newAges = [...room.childAges];
    if (count > newAges.length) {
      while (newAges.length < count) newAges.push(2);
    } else {
      newAges.splice(count);
    }
    onChange(value.map((r, i) => i === index ? { ...r, children: count, childAges: newAges } : r));
  }

  function updateChildAge(roomIndex: number, childIndex: number, age: number) {
    const room = value[roomIndex];
    const newAges = [...room.childAges];
    newAges[childIndex] = age;
    onChange(value.map((r, i) => i === roomIndex ? { ...r, childAges: newAges } : r));
  }

  function addRoom() {
    if (value.length < maxRooms) {
      onChange([...value, { adults: 2, children: 0, childAges: [] }]);
    }
  }

  function removeRoom(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        className="flex items-center gap-2 w-full text-left"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Select guests and rooms"
      >
        <i className="fa-light fa-user sb-field__icon" aria-hidden="true" />
        <span className="sb-occ-summary truncate flex-1">{summarizeOccupancy(value)}</span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'} text-info text-xs flex-shrink-0`} aria-hidden="true" />
      </button>

      {open && (
        <div className="sb-occ-panel" role="dialog" aria-label="Guests and rooms">
          <div className="sb-occ-rooms">
            {value.map((room, i) => (
              <div key={i} className="sb-occ-room">
                <div className="sb-occ-room-header">
                  <span className="sb-occ-room-label">Room {i + 1}</span>
                  {value.length > 1 && (
                    <button type="button" onClick={() => removeRoom(i)} className="sb-occ-remove" aria-label={`Remove room ${i + 1}`}>Remove</button>
                  )}
                </div>
                <hr className="sb-occ-divider" />
                <Stepper label="Adults" sub="Age 18+" value={room.adults} min={1} max={maxAdults} onChange={(v) => updateAdults(i, v)} />
                <hr className="sb-occ-divider" />
                <Stepper label="Children" sub="Ages 0–17" value={room.children} min={0} max={maxChildren} onChange={(v) => updateChildren(i, v)} />
                {room.childAges.length > 0 && (
                  <div className="sb-occ-ages">
                    {room.childAges.map((age, ci) => (
                      <div key={ci} className="sb-occ-age-row">
                        <span className="sb-occ-age-label">Child {ci + 1} age</span>
                        <select value={age} onChange={(e) => updateChildAge(i, ci, Number(e.target.value))} className="sb-occ-age-select" aria-label={`Age of child ${ci + 1} in room ${i + 1}`}>
                          {CHILD_AGES.map((a) => (
                            <option key={a} value={a}>{a === 0 ? 'Under 1' : `${a} yr${a !== 1 ? 's' : ''}`}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="sb-occ-footer">
            {value.length < maxRooms && (
              <button type="button" onClick={addRoom} className="sb-occ-add-btn">
                <i className="fas fa-plus-circle mr-1.5" aria-hidden="true" />
                Add another room
              </button>
            )}
            <button type="button" onClick={() => onOpenChange(false)} className="sb-occ-done-btn">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
